import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ErrorService } from '../shared/services/error.service';
import {
  PreventiveMaintenance,
  PreventiveMaintenanceCreate,
  PreventiveMaintenanceUpdate,
  PreventiveMaintenanceItem,
  PreventiveMaintenanceItemCreate,
  PreventiveMaintenanceItemUpdate,
  ApiResponse,
  PaginatedResponse,
  PreventiveMaintenanceFilter,
  ByMileageFilter,
  IdentifyPreventiveMaintenanceFilter,
  StockItem,
  IdentifiedPreventiveMaintenance
} from '../models/PreventiveMaintenance';

@Injectable({
  providedIn: 'root'
})
export class PreventiveMaintenanceService {
  // Usa o proxy em desenvolvimento (redireciona para localhost:7071) ou URL direta em produção
  private readonly API_URL = `${environment.microservices.operation}/preventive-maintenance`;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  // ===== CRUD OPERATIONS =====

  /**
   * Criar nova preventiva
   */
  create(preventiveMaintenance: PreventiveMaintenanceCreate): Observable<ApiResponse<PreventiveMaintenance>> {
    return this.http.post<ApiResponse<PreventiveMaintenance>>(
      this.API_URL,
      preventiveMaintenance
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao criar manutenção preventiva');
        return throwError(() => err);
      })
    );
  }

  /**
   * Buscar preventiva por ID
   */
  getById(id: number, includeItems: boolean = true): Observable<any> {
    const params = new HttpParams().set('includeItems', includeItems.toString());
    return this.http.get<any>(
      `${this.API_URL}/${id}`,
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar manutenção preventiva');
        return throwError(() => err);
      })
    );
  }

  /**
   * Listar todas as preventivas (paginado)
   */
  getAll(page: number = 1, pageSize: number = 20): Observable<PaginatedResponse<PreventiveMaintenance>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    return this.http.get<any>(
      this.API_URL,
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar manutenções preventivas');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualizar preventiva
   */
  update(id: number, preventiveMaintenance: PreventiveMaintenanceUpdate): Observable<ApiResponse<PreventiveMaintenance>> {
    return this.http.put<ApiResponse<PreventiveMaintenance>>(
      `${this.API_URL}/${id}`,
      preventiveMaintenance
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar manutenção preventiva');
        return throwError(() => err);
      })
    );
  }

  /**
   * Excluir preventiva (soft delete)
   */
  delete(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.API_URL}/${id}`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao excluir manutenção preventiva');
        return throwError(() => err);
      })
    );
  }

  // ===== CONSULTAS POR FILTROS =====

  /**
   * Buscar preventivas por tipo de veículo
   */
  getByVehicleType(vehicleType: string): Observable<ApiResponse<PreventiveMaintenance[]>> {
    return this.http.get<ApiResponse<PreventiveMaintenance[]>>(
      `${this.API_URL}/vehicle-type/${encodeURIComponent(vehicleType)}`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar manutenções por tipo de veículo');
        return throwError(() => err);
      })
    );
  }

  /**
   * Listar apenas preventivas ativas
   */
  getActive(): Observable<ApiResponse<PreventiveMaintenance[]>> {
    return this.http.get<ApiResponse<PreventiveMaintenance[]>>(
      `${this.API_URL}/active`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar manutenções ativas');
        return throwError(() => err);
      })
    );
  }

  /**
   * Buscar preventivas por quilometragem
   */
  getByMileage(filter: ByMileageFilter): Observable<ApiResponse<PreventiveMaintenance[]>> {
    let params = new HttpParams()
      .set('currentMileage', filter.currentMileage.toString());

    if (filter.vehicleType) {
      params = params.set('vehicleType', filter.vehicleType);
    }

    return this.http.get<ApiResponse<PreventiveMaintenance[]>>(
      `${this.API_URL}/by-mileage`,
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar manutenções por quilometragem');
        return throwError(() => err);
      })
    );
  }

  // ===== GERENCIAMENTO DE ITENS =====

  /**
   * Adicionar item a uma preventiva
   */
  addItem(
    preventiveMaintenanceId: number,
    item: PreventiveMaintenanceItemCreate
  ): Observable<any> {
    return this.http.post<any>(
      `${this.API_URL}/${preventiveMaintenanceId}/items`,
      item
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao adicionar item à manutenção');
        return throwError(() => err);
      })
    );
  }

  /**
   * Listar itens de uma preventiva
   */
  getItems(preventiveMaintenanceId: number): Observable<ApiResponse<PreventiveMaintenanceItem[]>> {
    return this.http.get<ApiResponse<PreventiveMaintenanceItem[]>>(
      `${this.API_URL}/${preventiveMaintenanceId}/items`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar itens da manutenção');
        return throwError(() => err);
      })
    );
  }

  /**
   * Atualizar item de uma preventiva
   */
  updateItem(
    preventiveMaintenanceId: number,
    itemId: number,
    item: PreventiveMaintenanceItemUpdate
  ): Observable<ApiResponse<PreventiveMaintenanceItem>> {
    return this.http.put<ApiResponse<PreventiveMaintenanceItem>>(
      `${this.API_URL}/${preventiveMaintenanceId}/items/${itemId}`,
      item
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao atualizar item da manutenção');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remover item de uma preventiva
   */
  removeItem(preventiveMaintenanceId: number, itemId: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      `${this.API_URL}/${preventiveMaintenanceId}/items/${itemId}`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao remover item da manutenção');
        return throwError(() => err);
      })
    );
  }

  // ===== INTEGRAÇÃO COM ESTOQUE =====

  /**
   * Listar itens do estoque disponíveis
   */
  getStockItems(warehouseId?: number, itemName?: string, itemId?: number): Observable<any> {
    let params = new HttpParams();
    if (warehouseId) {
      params = params.set('warehouseId', warehouseId.toString());
    }
    if (itemName) {
      params = params.set('itemName', itemName);
    }
    if (itemId) {
      params = params.set('itemId', itemId.toString());
    }

    return this.http.get<any>(
      `${this.API_URL}/stock-items`,
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar itens do estoque');
        return throwError(() => err);
      })
    );
  }

  /**
   * Buscar item específico do estoque por ID
   */
  getStockItemById(stockItemId: number): Observable<ApiResponse<StockItem>> {
    return this.http.get<ApiResponse<StockItem>>(
      `${this.API_URL}/stock-items/${stockItemId}`
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao buscar item do estoque');
        return throwError(() => err);
      })
    );
  }

  // ===== IDENTIFICAÇÃO INTELIGENTE =====

  /**
   * Identificar preventivas necessárias para um veículo
   */
  identify(filter: IdentifyPreventiveMaintenanceFilter): Observable<ApiResponse<IdentifiedPreventiveMaintenance[]>> {
    const params = new HttpParams()
      .set('vehicleId', filter.vehicleId.toString())
      .set('currentMileage', filter.currentMileage.toString());

    return this.http.get<ApiResponse<IdentifiedPreventiveMaintenance[]>>(
      `${this.API_URL}/identify`,
      { params }
    ).pipe(
      catchError(err => {
        this.errorService.showErrorMessage('Erro ao identificar manutenções necessárias');
        return throwError(() => err);
      })
    );
  }
}

